import React, { useEffect, useState, useRef, Fragment } from 'react';
import { PencilIcon, TrashIcon, ChevronUpIcon, ChevronDownIcon, CodeIcon, TrendingUpIcon, TrendingDownIcon, Trash2Icon, FileTextIcon, PlusCircleIcon, Edit3Icon, PackageIcon, CheckCircle2Icon, AlertCircleIcon, LinkIcon, ChevronRightIcon, ChevronsDownIcon, ChevronsUpIcon, XIcon, LayersIcon, HistoryIcon, SaveIcon, UnlinkIcon } from 'lucide-react';
import { InvoiceDocument, LineItem, PurchaseOrderMatch, GoodsReceiptMatch } from '../types/invoice';
import { SelectedGLDimensions } from '../types/glCoding';
import { Button } from './ui/Button';
import { GLCodingModal } from './GLCodingModal';
import { POMatchingModal } from './POMatchingModal';
import { TaxCodeSelector } from './TaxCodeSelector';
import { BulkGLCodingModal } from './BulkGLCodingModal';
import { PreviousCodingModal } from './PreviousCodingModal';
import { ValidationResult } from '../utils/invoiceValidation';
import { handleTaxCodeChange } from '../utils/taxCalculations';
import { formatCurrency, formatAmount } from '../utils/currencyFormat';
interface LineItemsPanelProps {
  document: InvoiceDocument;
  onUpdateLineItem?: (index: number, updatedItem: LineItem) => void;
  validation?: ValidationResult;
  isEditAllMode?: boolean;
}
export function LineItemsPanel({
  document,
  onUpdateLineItem,
  validation,
  isEditAllMode = false
}: LineItemsPanelProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedLineItemIndex, setSelectedLineItemIndex] = useState<number | null>(null);
  const [poMatchingLineIndex, setPOMatchingLineIndex] = useState<number | null>(null);
  const [expandedLineItems, setExpandedLineItems] = useState<Set<number>>(new Set());
  const [showBulkGLModal, setShowBulkGLModal] = useState(false);
  const [showPreviousCodingModal, setShowPreviousCodingModal] = useState(false);
  // Initialize expanded line items for PO invoices on mount
  useEffect(() => {
    // For PO invoices (2-way or 3-way), expand all matched lines by default
    if (document.matchingType === '2-way' || document.matchingType === '3-way') {
      const matchedIndices = document.lineItems.map((item, index) => item.poMatch || item.grMatch || item.grMatches ? index : -1).filter(index => index !== -1);
      setExpandedLineItems(new Set(matchedIndices));
    }
  }, [document.matchingType, document.lineItems.length]);
  // State for inline editing
  const [editingLineIndex, setEditingLineIndex] = useState<number | null>(null);
  const [editedLineItem, setEditedLineItem] = useState<LineItem | null>(null);
  // State for new line being added
  const [newLineItem, setNewLineItem] = useState<LineItem | null>(null);
  const [isAddingNewLine, setIsAddingNewLine] = useState(false);
  // Draft state for Edit All mode - stores all pending changes
  const [draftLineItems, setDraftLineItems] = useState<LineItem[]>([]);
  // Ref for first input field to auto-focus when entering edit mode
  const firstInputRef = useRef<HTMLInputElement>(null);
  // State for delete confirmation
  const [deletingLineIndex, setDeletingLineIndex] = useState<number | null>(null);
  // Sync with Edit All mode
  useEffect(() => {
    if (isEditAllMode) {
      setIsEditMode(true);
      // Initialize draft with current line items
      setDraftLineItems([...document.lineItems]);
    } else {
      setIsEditMode(false);
      setEditingLineIndex(null);
      setEditedLineItem(null);
      setIsAddingNewLine(false);
      setNewLineItem(null);
      setDraftLineItems([]);
    }
  }, [isEditAllMode]);
  // Sync draft line items when document changes, but only if not in edit mode
  useEffect(() => {
    if (!isEditMode) {
      setDraftLineItems([...document.lineItems]);
    }
  }, [document.lineItems, isEditMode]);
  // Auto-focus first input when entering edit mode
  useEffect(() => {
    if (isEditMode && firstInputRef.current) {
      // Use setTimeout to ensure the input is rendered before focusing
      setTimeout(() => {
        firstInputRef.current?.focus();
      }, 0);
    }
  }, [isEditMode]);
  const toggleLineItemExpanded = (index: number) => {
    const newExpanded = new Set(expandedLineItems);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedLineItems(newExpanded);
  };
  const expandAllLineItems = () => {
    const allMatchedIndices = document.lineItems.map((item, index) => item.poMatch || item.grMatch ? index : -1).filter(index => index !== -1);
    setExpandedLineItems(new Set(allMatchedIndices));
  };
  const collapseAllLineItems = () => {
    setExpandedLineItems(new Set());
  };
  const handleEditModeToggle = () => {
    if (!isEditAllMode) {
      const newEditMode = !isEditMode;
      setIsEditMode(newEditMode);
      // When entering edit mode, put all lines into edit mode and initialize draft
      if (newEditMode) {
        setDraftLineItems([...document.lineItems]);
        setEditingLineIndex(null);
        setEditedLineItem(null);
      } else {
        // When exiting edit mode, clear everything
        setEditingLineIndex(null);
        setEditedLineItem(null);
        setIsAddingNewLine(false);
        setNewLineItem(null);
        setDraftLineItems([]);
      }
    }
  };
  const handleSaveAllChanges = () => {
    if (!onUpdateLineItem) return;
    // Apply all draft changes
    draftLineItems.forEach((item, index) => {
      onUpdateLineItem(index, item);
    });
    // Exit edit mode
    setIsEditMode(false);
    setDraftLineItems([]);
  };
  const handleCancelAllChanges = () => {
    // Discard all draft changes
    setIsEditMode(false);
    setDraftLineItems([]);
    setIsAddingNewLine(false);
    setNewLineItem(null);
  };
  const updateDraftLineItem = (index: number, updatedItem: LineItem) => {
    setDraftLineItems(prev => {
      const updated = [...prev];
      updated[index] = updatedItem;
      return updated;
    });
  };
  const handleAddNewLine = () => {
    const newLine: LineItem = {
      id: `line-new-${Date.now()}`,
      itemCode: '',
      description: '',
      quantity: 1,
      unitPrice: 0,
      subtotal: 0,
      taxCode: 'I',
      taxAmt: 0,
      total: 0,
      type: 'CR',
      glCode: ''
    };
    setNewLineItem(newLine);
    setIsAddingNewLine(true);
  };
  const handleSaveNewLine = () => {
    if (!newLineItem || !onUpdateLineItem) return;
    // Add the new line at the end
    const newIndex = document.lineItems.length;
    onUpdateLineItem(newIndex, newLineItem);
    // Reset state
    setIsAddingNewLine(false);
    setNewLineItem(null);
  };
  const handleCancelNewLine = () => {
    setIsAddingNewLine(false);
    setNewLineItem(null);
  };
  const updateNewLineField = (field: keyof LineItem, value: any) => {
    if (!newLineItem) return;
    const updated = {
      ...newLineItem,
      [field]: value
    };
    // Recalculate amounts if quantity or unit price changes
    if (field === 'quantity' || field === 'unitPrice') {
      const qty = parseFloat(field === 'quantity' ? value : updated.quantity) || 0;
      const price = parseFloat(field === 'unitPrice' ? value : updated.unitPrice) || 0;
      const subtotal = qty * price;
      // Calculate tax based on tax code
      let taxRate = 0.1; // Default 10% for 'I'
      if (updated.taxCode === 'E') taxRate = 0;
      if (updated.taxCode === 'Z') taxRate = 0;
      const taxAmt = subtotal * taxRate;
      const total = subtotal + taxAmt;
      updated.quantity = qty;
      updated.unitPrice = price;
      updated.subtotal = subtotal;
      updated.taxAmt = taxAmt;
      updated.total = total;
    }
    setNewLineItem(updated);
  };
  const handleEditLineClick = (index: number) => {
    setEditingLineIndex(index);
    setEditedLineItem({
      ...document.lineItems[index]
    });
  };
  const handleSaveLineEdit = () => {
    if (editingLineIndex !== null && editedLineItem && onUpdateLineItem) {
      onUpdateLineItem(editingLineIndex, editedLineItem);
      setEditingLineIndex(null);
      setEditedLineItem(null);
    }
  };
  const handleCancelLineEdit = () => {
    setEditingLineIndex(null);
    setEditedLineItem(null);
  };
  const handleDeleteLine = (index: number) => {
    if (isEditMode && draftLineItems.length > 0) {
      // Remove from draft state
      const updatedDraft = draftLineItems.filter((_, i) => i !== index);
      setDraftLineItems(updatedDraft);
    } else if (onUpdateLineItem) {
      // For immediate deletion (non-edit mode), would need parent support
      // This would require a new prop like onDeleteLineItem
      console.log('Delete line:', index);
    }
    setDeletingLineIndex(null);
  };
  const handleCancelDelete = () => {
    setDeletingLineIndex(null);
  };
  const updateEditedField = (field: keyof LineItem, value: any) => {
    if (!editedLineItem) return;
    const updated = {
      ...editedLineItem,
      [field]: value
    };
    // Recalculate amounts if quantity or unit price changes
    if (field === 'quantity' || field === 'unitPrice') {
      const qty = parseFloat(field === 'quantity' ? value : updated.quantity) || 0;
      const price = parseFloat(field === 'unitPrice' ? value : updated.unitPrice) || 0;
      const subtotal = qty * price;
      // Calculate tax based on tax code
      let taxRate = 0.1; // Default 10% for 'I'
      if (updated.taxCode === 'E') taxRate = 0;
      if (updated.taxCode === 'Z') taxRate = 0;
      const taxAmt = subtotal * taxRate;
      const total = subtotal + taxAmt;
      updated.quantity = qty;
      updated.unitPrice = price;
      updated.subtotal = subtotal;
      updated.taxAmt = taxAmt;
      updated.total = total;
    }
    setEditedLineItem(updated);
  };
  const handleGLCodeClick = (index: number) => {
    if (!isEditMode) return;
    setSelectedLineItemIndex(index);
  };
  const handleGLCodeApply = (glCode: string, dimensions: SelectedGLDimensions) => {
    if (selectedLineItemIndex === -1) {
      // Applying to new line
      if (newLineItem) {
        setNewLineItem({
          ...newLineItem,
          glCode
        });
      }
    } else if (selectedLineItemIndex !== null && onUpdateLineItem) {
      // Applying to existing line
      const updatedItem = {
        ...document.lineItems[selectedLineItemIndex],
        glCode
      };
      onUpdateLineItem(selectedLineItemIndex, updatedItem);
    }
    setSelectedLineItemIndex(null);
  };
  const handleGLCodeClose = () => {
    setSelectedLineItemIndex(null);
  };
  const handleBulkGLApply = (glCode: string, dimensions: SelectedGLDimensions) => {
    if (!onUpdateLineItem) return;
    for (let i = document.lineItems.length - 1; i >= 0; i--) {
      const updatedItem = {
        ...document.lineItems[i],
        glCode
      };
      onUpdateLineItem(i, updatedItem);
    }
    setShowBulkGLModal(false);
  };
  const handlePreviousCodingApply = (glCode: string) => {
    if (!onUpdateLineItem) return;
    for (let i = document.lineItems.length - 1; i >= 0; i--) {
      const updatedItem = {
        ...document.lineItems[i],
        glCode
      };
      onUpdateLineItem(i, updatedItem);
    }
    setShowPreviousCodingModal(false);
  };
  const handlePOMatchClick = (index: number) => {
    if (!isEditMode) return;
    setPOMatchingLineIndex(index);
  };
  const handlePOMatchApply = (poMatch: PurchaseOrderMatch, grMatches?: GoodsReceiptMatch[]) => {
    if (poMatchingLineIndex !== null && onUpdateLineItem) {
      const currentItem = document.lineItems[poMatchingLineIndex];
      const updatedItem: LineItem = {
        ...currentItem,
        poMatch,
        grMatches,
        quantity: poMatch.matchedQuantity,
        unitPrice: poMatch.poUnitPrice,
        subtotal: poMatch.matchedAmount / 1.1,
        taxAmt: poMatch.matchedAmount - poMatch.matchedAmount / 1.1,
        total: poMatch.matchedAmount
      };
      onUpdateLineItem(poMatchingLineIndex, updatedItem);
    }
    setPOMatchingLineIndex(null);
  };
  const handlePOMatchClose = () => {
    setPOMatchingLineIndex(null);
  };
  const handleTaxCodeChangeForItem = (index: number, newTaxCode: string) => {
    if (!isEditMode) return;
    const currentItem = isEditMode && draftLineItems.length > 0 ? draftLineItems[index] : document.lineItems[index];
    const recalculated = handleTaxCodeChange(currentItem.subtotal, currentItem.taxAmt, currentItem.total, newTaxCode, currentItem.taxCode);
    const updatedItem = {
      ...currentItem,
      taxCode: newTaxCode,
      subtotal: recalculated.subtotal,
      taxAmt: recalculated.taxAmt,
      unitPrice: recalculated.subtotal / currentItem.quantity
    };
    // Update draft state instead of calling onUpdateLineItem
    updateDraftLineItem(index, updatedItem);
  };
  const taxCodeErrors = validation?.errors.filter(e => e.field === 'taxCode' || e.field === 'taxAmt') || [];
  const hasTaxCodeError = taxCodeErrors.length > 0;
  const taxCodeErrorMessage = taxCodeErrors.map(e => e.message).join('. ');
  const matchingType = document.matchingType;
  const isNonPO = matchingType === 'non-po';
  const hasAnyMatchedLines = document.lineItems.some(item => item.poMatch || item.grMatch);
  const allMatchedLinesExpanded = hasAnyMatchedLines && document.lineItems.every((item, index) => !(item.poMatch || item.grMatch) || expandedLineItems.has(index));
  // Combine existing line items with new line item if adding
  const displayLineItems = isAddingNewLine && newLineItem ? [...(isEditMode && draftLineItems.length > 0 ? draftLineItems : document.lineItems), newLineItem] : isEditMode && draftLineItems.length > 0 ? draftLineItems : document.lineItems;
  return <>
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm" data-id="element-1364">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50" data-id="element-1365">
          <div className="flex items-center gap-3" data-id="element-1366">
            <button onClick={() => setIsExpanded(!isExpanded)} className="p-1 hover:bg-gray-200 rounded transition-colors" aria-label={isExpanded ? 'Collapse line items' : 'Expand line items'} data-id="element-1367">
              {isExpanded ? <ChevronUpIcon className="w-5 h-5 text-gray-600" data-id="element-1368" /> : <ChevronDownIcon className="w-5 h-5 text-gray-600" data-id="element-1369" />}
            </button>
            <h3 className="text-sm font-semibold text-gray-900" data-id="element-1370">
              Lines ({displayLineItems.length})
            </h3>
            {document.matchingType && <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${document.matchingType === '3-way' ? 'bg-green-100 text-green-700' : document.matchingType === '2-way' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'}`} data-id="element-1371">
                {document.matchingType === '3-way' && '3-Way Match'}
                {document.matchingType === '2-way' && '2-Way Match'}
                {document.matchingType === 'non-po' && 'Non-PO'}
              </span>}
            {isEditMode && <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-700" data-id="element-1372">
                Editing
              </span>}

            {/* Divider and Summary */}
            <div className="h-6 w-px bg-gray-300 mx-1" data-id="element-1373"></div>
            <div className="flex items-center gap-6 text-sm" data-id="element-1374">
              <div className="flex items-baseline gap-1.5" data-id="element-1375">
                <span className="text-gray-600" data-id="element-1376">Subtotal:</span>
                <span className="font-semibold text-gray-900" data-id="element-1377">
                  {formatCurrency(document.subtotal, document.currency, true)}
                </span>
              </div>
              <div className="flex items-baseline gap-1.5" data-id="element-1378">
                <span className="text-gray-600" data-id="element-1379">Tax:</span>
                <span className="font-semibold text-gray-900" data-id="element-1380">
                  {formatCurrency(document.taxAmt, document.currency, true)}
                </span>
              </div>
              <div className="flex items-baseline gap-1.5" data-id="element-1381">
                <span className="text-gray-600" data-id="element-1382">Total:</span>
                <span className="font-semibold text-gray-900" data-id="element-1383">
                  {formatCurrency(document.total, document.currency, true)}
                </span>
              </div>
            </div>
          </div>

          {isExpanded && <div className="flex gap-2" data-id="element-1384">
              {isEditMode && isNonPO && <>
                  <Button variant="ghost" size="sm" onClick={() => setShowPreviousCodingModal(true)} data-id="element-1385">
                    <HistoryIcon className="w-4 h-4 mr-1.5" data-id="element-1386" />
                    Previous Coding
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setShowBulkGLModal(true)} data-id="element-1387">
                    <LayersIcon className="w-4 h-4 mr-1.5" data-id="element-1388" />
                    Apply GL to All
                  </Button>
                  <div className="w-px h-6 bg-gray-300" data-id="element-1389"></div>
                </>}

              {(matchingType === '2-way' || matchingType === '3-way') && hasAnyMatchedLines && <>
                    {allMatchedLinesExpanded ? <Button variant="ghost" size="sm" onClick={collapseAllLineItems} data-id="element-1390">
                        <ChevronsUpIcon className="w-4 h-4 mr-1.5" data-id="element-1391" />
                        Collapse All
                      </Button> : <Button variant="ghost" size="sm" onClick={expandAllLineItems} data-id="element-1392">
                        <ChevronsDownIcon className="w-4 h-4 mr-1.5" data-id="element-1393" />
                        Expand All
                      </Button>}
                    <div className="w-px h-6 bg-gray-300" data-id="element-1394"></div>
                  </>}

              {isEditMode && <>
                  <Button variant="ghost" size="sm" data-id="element-1395">
                    <Trash2Icon className="w-4 h-4 mr-1.5" data-id="element-1396" />
                    Delete All
                  </Button>
                  {isNonPO && <Button variant="ghost" size="sm" data-id="element-1397">
                      <FileTextIcon className="w-4 h-4 mr-1.5" data-id="element-1398" />
                      Create Line From Header
                    </Button>}
                  <Button variant="ghost" size="sm" onClick={handleAddNewLine} disabled={isAddingNewLine} data-id="element-1399">
                    <PlusCircleIcon className="w-4 h-4 mr-1.5" data-id="element-1400" />
                    Add New Line
                  </Button>
                  <div className="w-px h-6 bg-gray-300" data-id="element-1401"></div>
                </>}

              {isEditMode && !isEditAllMode ? <>
                  <button onClick={handleSaveAllChanges} className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded transition-colors" data-id="element-1402">
                    <CheckCircle2Icon className="w-4 h-4" data-id="element-1403" />
                    Save
                  </button>
                  <button onClick={handleCancelAllChanges} className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded transition-colors" data-id="element-1404">
                    <XIcon className="w-4 h-4" data-id="element-1405" />
                    Cancel
                  </button>
                </> : !isEditMode ? <Button variant="ghost" size="sm" onClick={handleEditModeToggle} data-id="element-1406">
                  <Edit3Icon className="w-4 h-4 mr-1.5" data-id="element-1407" />
                  Edit
                </Button> : null}
            </div>}
        </div>

        {/* Table content */}
        {isExpanded && <div className="p-6" data-id="element-1408">
            <div className="border border-gray-200 rounded-lg" data-id="element-1409">
              <div className="overflow-x-auto" data-id="element-1410">
                <table className="w-full text-sm" data-id="element-1411">
                  <thead className="bg-gray-50" data-id="element-1412">
                    <tr data-id="element-1413">
                      {(matchingType === '2-way' || matchingType === '3-way') && <th className="px-2 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider whitespace-nowrap border-b border-gray-200 w-8" data-id="element-1414">
                          {/* Expand toggle column */}
                        </th>}
                      {!isNonPO && <th className="px-2 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider whitespace-nowrap border-b border-gray-200" data-id="element-1415">
                          Item Code
                        </th>}
                      <th className="px-2 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider whitespace-nowrap border-b border-gray-200" data-id="element-1416">
                        Description
                      </th>
                      <th className="px-2 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider whitespace-nowrap border-b border-gray-200" data-id="element-1417">
                        GL Code
                      </th>
                      <th className="px-2 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider whitespace-nowrap border-b border-gray-200" data-id="element-1418">
                        Tax Code
                      </th>
                      <th className="px-2 py-2 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider whitespace-nowrap border-b border-gray-200" data-id="element-1419">
                        Qty
                      </th>
                      <th className="px-2 py-2 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider whitespace-nowrap border-b border-gray-200" data-id="element-1420">
                        Unit Price
                      </th>
                      <th className="px-2 py-2 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider whitespace-nowrap border-b border-gray-200" data-id="element-1421">
                        Subtotal
                      </th>
                      <th className="px-2 py-2 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider whitespace-nowrap border-b border-gray-200" data-id="element-1422">
                        Tax
                      </th>
                      <th className="px-2 py-2 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider whitespace-nowrap border-b border-gray-200" data-id="element-1423">
                        Total
                      </th>
                      <th className="px-2 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider whitespace-nowrap border-b border-gray-200" data-id="element-1424">
                        Type
                      </th>
                      {isEditMode && <th className="px-2 py-2 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider whitespace-nowrap border-b border-gray-200" data-id="element-1425">
                          Actions
                        </th>}
                    </tr>
                  </thead>
                  <tbody className="bg-white" data-id="element-1426">
                    {displayLineItems.map((item, index) => {
                  const isNewLine = isAddingNewLine && index === displayLineItems.length - 1;
                  const isCredit = item.type === 'CR';
                  const hasMatching = item.poMatch || item.grMatch || item.grMatches && item.grMatches.length > 0;
                  const isLineExpanded = expandedLineItems.has(index);
                  const isFullyMatched = item.poMatch?.remainingQuantity === 0;
                  const isEditing = isEditMode && !isNewLine;
                  const displayItem = isEditing && editedLineItem ? editedLineItem : isNewLine && newLineItem ? newLineItem : item;
                  let rowBgClass = '';
                  if (isNewLine) {
                    rowBgClass = 'bg-green-50';
                  } else if (hasMatching && !isLineExpanded) {
                    rowBgClass = 'bg-blue-50';
                  }
                  const hoverClass = hasMatching ? 'hover:bg-blue-100' : 'hover:bg-gray-50';
                  return <Fragment key={isNewLine ? 'new-line' : index} data-id="element-1427">
                          {/* Main Invoice Line Row */}
                          <tr className={`border-b border-gray-100 ${rowBgClass} ${hoverClass} transition-colors`} data-id="element-1428">
                            {/* Expand Toggle */}
                            {(matchingType === '2-way' || matchingType === '3-way') && <td className="px-2 py-3 whitespace-nowrap align-top" data-id="element-1429">
                                {hasMatching && !isNewLine && <button onClick={() => toggleLineItemExpanded(index)} className="p-1 hover:bg-gray-200 rounded transition-all" title={isLineExpanded ? 'Hide matching details' : 'Show matching details'} data-id="element-1430">
                                    <ChevronRightIcon className={`w-4 h-4 text-gray-600 transition-transform ${isLineExpanded ? 'rotate-90' : ''}`} data-id="element-1431" />
                                  </button>}
                              </td>}

                            {/* Item Code - Hidden for Non-PO */}
                            {!isNonPO && <td className="px-2 py-3 whitespace-nowrap align-top" style={{
                        minWidth: '100px'
                      }} data-id="element-1432">
                                {isEditing || isNewLine ? <input ref={index === 0 ? firstInputRef : null} type="text" value={displayItem.itemCode} onChange={e => isNewLine ? updateNewLineField('itemCode', e.target.value) : updateEditedField('itemCode', e.target.value)} className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Item code" data-id="element-1433" /> : <div className="flex items-center gap-2" data-id="element-1434">
                                    <span className="font-medium text-gray-900" data-id="element-1435">
                                      {item.itemCode}
                                    </span>
                                    {hasMatching && <span className="flex items-center gap-1 text-xs text-green-700" data-id="element-1436">
                                        {isFullyMatched ? <CheckCircle2Icon className="w-3.5 h-3.5" data-id="element-1437" /> : <AlertCircleIcon className="w-3.5 h-3.5 text-amber-600" data-id="element-1438" />}
                                      </span>}
                                  </div>}
                              </td>}

                            {/* Description */}
                            <td className="px-2 py-3 align-top" style={{
                        minWidth: '200px'
                      }} data-id="element-1439">
                              {isEditing || isNewLine ? <textarea ref={isNonPO && index === 0 ? firstInputRef as any : null} value={displayItem.description} onChange={e => isNewLine ? updateNewLineField('description', e.target.value) : updateEditedField('description', e.target.value)} className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Description" rows={2} data-id="element-1440" /> : <>
                                  <div className="text-gray-900 font-medium" data-id="element-1441">
                                    {item.description}
                                  </div>
                                  {item.notes && <div className="text-xs text-gray-500 mt-1 flex items-start gap-1" data-id="element-1442">
                                      <span className="text-gray-400" data-id="element-1443">↪</span>
                                      <span data-id="element-1444">{item.notes}</span>
                                    </div>}
                                </>}
                            </td>

                            {/* GL Code */}
                            <td className="px-2 py-3 whitespace-nowrap align-top" style={{
                        minWidth: '120px',
                        maxWidth: '160px'
                      }} data-id="element-1445">
                              {isEditMode && !isNewLine ? <button onClick={() => handleGLCodeClick(index)} className="w-full text-left" data-id="element-1446">
                                  {item.glCode ? <code className="text-sm font-mono bg-green-50 text-green-700 px-2 py-1 rounded border border-green-200 inline-block hover:bg-green-100 transition-colors" data-id="element-1447">
                                      {item.glCode}
                                    </code> : <div className="flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-800 hover:underline font-medium transition-colors" data-id="element-1448">
                                      <CodeIcon className="w-3.5 h-3.5" data-id="element-1449" />
                                      Add GL
                                    </div>}
                                </button> : isNewLine ? <button onClick={() => {
                          setSelectedLineItemIndex(-1);
                        }} className="w-full text-left" data-id="element-1450">
                                  {displayItem.glCode ? <code className="text-sm font-mono bg-green-50 text-green-700 px-2 py-1 rounded border border-green-200 inline-block hover:bg-green-100 transition-colors" data-id="element-1451">
                                      {displayItem.glCode}
                                    </code> : <div className="flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-800 hover:underline font-medium transition-colors" data-id="element-1452">
                                      <CodeIcon className="w-3.5 h-3.5" data-id="element-1453" />
                                      Add GL
                                    </div>}
                                </button> : <>
                                  {item.glCode ? <code className="text-sm font-mono bg-green-50 text-green-700 px-2 py-1 rounded border border-green-200 inline-block" data-id="element-1454">
                                      {item.glCode}
                                    </code> : <span className="text-sm text-gray-400" data-id="element-1455">
                                      No GL
                                    </span>}
                                </>}
                            </td>

                            {/* Tax Code */}
                            <td className="px-2 py-3 align-top" style={{
                        width: '80px'
                      }} data-id="element-1456">
                              {isEditMode && !isNewLine ? <TaxCodeSelector selectedCode={item.taxCode} onChange={taxCode => handleTaxCodeChangeForItem(index, taxCode)} hasError={hasTaxCodeError} errorMessage={taxCodeErrorMessage} data-id="element-1457" /> : isNewLine ? <select value={displayItem.taxCode} onChange={e => updateNewLineField('taxCode', e.target.value)} className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-1458">
                                  <option value="I" data-id="element-1459">I</option>
                                  <option value="E" data-id="element-1460">E</option>
                                  <option value="Z" data-id="element-1461">Z</option>
                                </select> : <span className="text-sm text-gray-900" data-id="element-1462">
                                  {item.taxCode || '-'}
                                </span>}
                            </td>

                            {/* Quantity */}
                            <td className="px-2 py-3 text-right whitespace-nowrap align-top" style={{
                        width: '80px'
                      }} data-id="element-1463">
                              {isEditMode || isNewLine ? <input type="text" inputMode="numeric" value={displayItem.quantity} onChange={e => {
                          const value = e.target.value;
                          // Allow empty string or valid numbers
                          if (value === '' || /^\d+$/.test(value)) {
                            const numValue = value === '' ? 0 : parseInt(value, 10);
                            if (isNewLine) {
                              updateNewLineField('quantity', numValue);
                            } else {
                              // Update draft instead of saving immediately
                              const updatedItem = {
                                ...item,
                                quantity: numValue
                              };
                              // Recalculate amounts
                              const subtotal = numValue * item.unitPrice;
                              let taxRate = 0.1;
                              if (item.taxCode === 'E') taxRate = 0;
                              if (item.taxCode === 'Z') taxRate = 0;
                              const taxAmt = subtotal * taxRate;
                              const total = subtotal + taxAmt;
                              updatedItem.subtotal = subtotal;
                              updatedItem.taxAmt = taxAmt;
                              updatedItem.total = total;
                              updateDraftLineItem(index, updatedItem);
                            }
                          }
                        }} className="w-full px-2 py-1 border border-gray-300 rounded text-sm text-right focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="0" data-id="element-1464" /> : <span className="text-gray-900 font-medium" data-id="element-1465">
                                  {item.quantity}
                                </span>}
                            </td>

                            {/* Unit Price */}
                            <td className="px-2 py-3 text-right whitespace-nowrap align-top" style={{
                        width: '90px'
                      }} data-id="element-1466">
                              {isEditing || isNewLine ? <input type="text" inputMode="decimal" value={displayItem.unitPrice} onChange={e => {
                          const value = e.target.value;
                          if (value === '' || /^\d*\.?\d*$/.test(value)) {
                            isNewLine ? updateNewLineField('unitPrice', value) : updateEditedField('unitPrice', value);
                          }
                        }} className="w-full px-2 py-1 border border-gray-300 rounded text-sm text-right focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="0.00" data-id="element-1467" /> : <span className="text-gray-900" data-id="element-1468">
                                  {formatAmount(item.unitPrice, document.currency)}
                                </span>}
                            </td>

                            {/* Subtotal */}
                            <td className="px-2 py-3 text-right whitespace-nowrap align-top" style={{
                        width: '90px'
                      }} data-id="element-1469">
                              <span className="text-gray-900 font-medium" data-id="element-1470">
                                {formatAmount(displayItem.subtotal, document.currency)}
                              </span>
                            </td>

                            {/* Tax */}
                            <td className="px-2 py-3 text-right whitespace-nowrap align-top" style={{
                        width: '80px'
                      }} data-id="element-1471">
                              <span className="text-gray-600" data-id="element-1472">
                                {formatAmount(displayItem.taxAmt, document.currency)}
                              </span>
                            </td>

                            {/* Total */}
                            <td className="px-2 py-3 text-right whitespace-nowrap align-top" style={{
                        width: '90px'
                      }} data-id="element-1473">
                              <span className="text-gray-900 font-semibold" data-id="element-1474">
                                {formatAmount(displayItem.total, document.currency)}
                              </span>
                            </td>

                            {/* Type */}
                            <td className="px-2 py-3 whitespace-nowrap align-top" style={{
                        width: '90px'
                      }} data-id="element-1475">
                              {isEditing || isNewLine ? <select value={displayItem.type} onChange={e => isNewLine ? updateNewLineField('type', e.target.value as 'CR' | 'DR') : updateEditedField('type', e.target.value as 'CR' | 'DR')} className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-1476">
                                  <option value="CR" data-id="element-1477">CR</option>
                                  <option value="DR" data-id="element-1478">DR</option>
                                </select> : <span className={`inline-flex items-center justify-center px-2 py-0.5 rounded text-xs font-semibold ${isCredit ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`} data-id="element-1479">
                                  {isCredit ? 'CR' : 'DR'}
                                </span>}
                            </td>

                            {/* Actions */}
                            {isEditMode && <td className="px-2 py-3 whitespace-nowrap align-top" style={{
                        width: '80px'
                      }} data-id="element-1480">
                                {isNewLine ? <div className="flex items-center justify-center gap-1" data-id="element-1481">
                                    <button onClick={handleSaveNewLine} className="p-1 hover:bg-green-50 rounded transition-colors" title="Save new line" data-id="element-1482">
                                      <SaveIcon className="w-4 h-4 text-green-600" data-id="element-1483" />
                                    </button>
                                    <button onClick={handleCancelNewLine} className="p-1 hover:bg-gray-100 rounded transition-colors" title="Cancel" data-id="element-1484">
                                      <XIcon className="w-4 h-4 text-gray-600" data-id="element-1485" />
                                    </button>
                                  </div> : deletingLineIndex === index ? <div className="flex items-center justify-center gap-1" data-id="element-1486">
                                    <button onClick={() => handleDeleteLine(index)} className="p-1 hover:bg-red-100 rounded transition-colors" title="Confirm delete" data-id="element-1487">
                                      <CheckCircle2Icon className="w-4 h-4 text-red-600" data-id="element-1488" />
                                    </button>
                                    <button onClick={handleCancelDelete} className="p-1 hover:bg-gray-100 rounded transition-colors" title="Cancel delete" data-id="element-1489">
                                      <XIcon className="w-4 h-4 text-gray-600" data-id="element-1490" />
                                    </button>
                                  </div> : <div className="flex items-center justify-center gap-1" data-id="element-1491">
                                    {hasMatching && <button onClick={() => {
                            // Clear PO and GR matching for this line
                            const updatedItem = {
                              ...item,
                              poMatch: undefined,
                              grMatch: undefined,
                              grMatches: undefined
                            };
                            updateDraftLineItem(index, updatedItem);
                          }} className="p-1 hover:bg-amber-50 rounded transition-colors" title="Clear PO/GR matching" data-id="element-1492">
                                        <UnlinkIcon className="w-4 h-4 text-amber-600" data-id="element-1493" />
                                      </button>}
                                    <button onClick={() => setDeletingLineIndex(index)} className="p-1 hover:bg-red-50 rounded transition-colors" title="Delete line item" data-id="element-1494">
                                      <TrashIcon className="w-4 h-4 text-red-500" data-id="element-1495" />
                                    </button>
                                  </div>}
                              </td>}
                          </tr>

                          {/* PO/GR Details - only for existing lines, not new line */}
                          {!isNewLine && isLineExpanded && item.poMatch && <tr className="bg-blue-50 border-b border-blue-100" data-id="element-1496">
                              {(matchingType === '2-way' || matchingType === '3-way') && <td className="px-4 py-4" data-id="element-1497"></td>}
                              <td colSpan={isEditMode ? 11 : 10} className="px-4 py-4" data-id="element-1498">
                                <div className="flex items-start gap-6" data-id="element-1499">
                                  {/* PO Match Details */}
                                  <div className="flex-1" data-id="element-1500">
                                    <div className="flex items-center justify-between mb-3" data-id="element-1501">
                                      <div className="flex items-center gap-2" data-id="element-1502">
                                        <div className="w-1 h-6 bg-blue-500 rounded" data-id="element-1503"></div>
                                        <h4 className="text-sm font-semibold text-blue-900" data-id="element-1504">
                                          Purchase Order Match
                                        </h4>
                                      </div>
                                      {isEditMode && <button onClick={() => handlePOMatchClick(index)} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-100 hover:bg-blue-200 rounded transition-colors" data-id="element-1505">
                                          <PencilIcon className="w-3.5 h-3.5" data-id="element-1506" />
                                          Edit PO Match
                                        </button>}
                                    </div>
                                    <div className="grid grid-cols-4 gap-4 text-xs" data-id="element-1507">
                                      <div data-id="element-1508">
                                        <div className="text-blue-700 font-medium mb-1" data-id="element-1509">
                                          PO Number
                                        </div>
                                        <code className="text-sm font-mono bg-blue-100 text-blue-800 px-2 py-1 rounded border border-blue-200" data-id="element-1510">
                                          {item.poMatch.poNumber}
                                        </code>
                                      </div>
                                      <div data-id="element-1511">
                                        <div className="text-blue-700 font-medium mb-1" data-id="element-1512">
                                          PO Line
                                        </div>
                                        <div className="text-gray-900 font-semibold" data-id="element-1513">
                                          Line {item.poMatch.poLineNumber}
                                        </div>
                                      </div>
                                      <div data-id="element-1514">
                                        <div className="text-blue-700 font-medium mb-1" data-id="element-1515">
                                          PO Description
                                        </div>
                                        <div className="text-gray-900" data-id="element-1516">
                                          {item.poMatch.poLineDescription}
                                        </div>
                                      </div>
                                      <div data-id="element-1517">
                                        <div className="text-blue-700 font-medium mb-1" data-id="element-1518">
                                          PO Unit Price
                                        </div>
                                        <div className="text-gray-900 font-semibold" data-id="element-1519">
                                          {formatAmount(item.poMatch.poUnitPrice, document.currency)}
                                        </div>
                                      </div>
                                      <div data-id="element-1520">
                                        <div className="text-blue-700 font-medium mb-1" data-id="element-1521">
                                          PO Quantity
                                        </div>
                                        <div className="text-gray-900 font-semibold" data-id="element-1522">
                                          {item.poMatch.poQuantity}
                                        </div>
                                      </div>
                                      <div data-id="element-1523">
                                        <div className="text-blue-700 font-medium mb-1" data-id="element-1524">
                                          Matched Quantity
                                        </div>
                                        <div className="text-green-700 font-semibold" data-id="element-1525">
                                          {item.poMatch.matchedQuantity}
                                        </div>
                                      </div>
                                      <div data-id="element-1526">
                                        <div className="text-blue-700 font-medium mb-1" data-id="element-1527">
                                          Remaining Quantity
                                        </div>
                                        <div className={`font-semibold ${isFullyMatched ? 'text-gray-400' : 'text-amber-700'}`} data-id="element-1528">
                                          {item.poMatch.remainingQuantity}
                                        </div>
                                      </div>
                                      <div data-id="element-1529">
                                        <div className="text-blue-700 font-medium mb-1" data-id="element-1530">
                                          Match Status
                                        </div>
                                        <div className="flex items-center gap-1" data-id="element-1531">
                                          {isFullyMatched ? <>
                                              <CheckCircle2Icon className="w-4 h-4 text-green-600" data-id="element-1532" />
                                              <span className="text-green-700 font-medium" data-id="element-1533">
                                                Fully Matched
                                              </span>
                                            </> : <>
                                              <AlertCircleIcon className="w-4 h-4 text-amber-600" data-id="element-1534" />
                                              <span className="text-amber-700 font-medium" data-id="element-1535">
                                                Partial Match
                                              </span>
                                            </>}
                                        </div>
                                      </div>
                                    </div>
                                  </div>

                                  {/* GR Match Details (3-way only) - Support multiple GRs */}
                                  {item.grMatches && item.grMatches.length > 0 && <div className="flex-1 border-l border-blue-200 pl-6" data-id="element-1536">
                                        <div className="flex items-center justify-between mb-3" data-id="element-1537">
                                          <div className="flex items-center gap-2" data-id="element-1538">
                                            <div className="w-1 h-6 bg-green-500 rounded" data-id="element-1539"></div>
                                            <h4 className="text-sm font-semibold text-green-900" data-id="element-1540">
                                              Goods Receipt
                                              {item.grMatches.length > 1 ? 's' : ''}{' '}
                                              ({item.grMatches.length})
                                            </h4>
                                          </div>
                                          {isEditMode && <button onClick={() => handlePOMatchClick(index)} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-green-700 bg-green-100 hover:bg-green-200 rounded transition-colors" data-id="element-1541">
                                              <PencilIcon className="w-3.5 h-3.5" data-id="element-1542" />
                                              Edit GR Match
                                            </button>}
                                        </div>
                                        <div className="space-y-3" data-id="element-1543">
                                          {item.grMatches.map((gr, grIndex) => <div key={gr.grNumber} className={`${grIndex > 0 ? 'pt-3 border-t border-green-200' : ''}`} data-id="element-1544">
                                              <div className="grid grid-cols-3 gap-4 text-xs" data-id="element-1545">
                                                <div data-id="element-1546">
                                                  <div className="text-green-700 font-medium mb-1" data-id="element-1547">
                                                    GR Number
                                                  </div>
                                                  <code className="text-sm font-mono bg-green-100 text-green-800 px-2 py-1 rounded border border-green-200" data-id="element-1548">
                                                    {gr.grNumber}
                                                  </code>
                                                </div>
                                                <div data-id="element-1549">
                                                  <div className="text-green-700 font-medium mb-1" data-id="element-1550">
                                                    Receipt Date
                                                  </div>
                                                  <div className="text-gray-900 font-semibold" data-id="element-1551">
                                                    {gr.grDate}
                                                  </div>
                                                </div>
                                                <div data-id="element-1552">
                                                  <div className="text-green-700 font-medium mb-1" data-id="element-1553">
                                                    Allocated Qty
                                                  </div>
                                                  <div className="text-green-700 font-semibold" data-id="element-1554">
                                                    {gr.allocatedQuantity}
                                                  </div>
                                                </div>
                                              </div>
                                            </div>)}
                                        </div>
                                        <div className="mt-3 flex items-center gap-1 text-xs text-green-700 font-medium" data-id="element-1555">
                                          <PackageIcon className="w-4 h-4" data-id="element-1556" />
                                          <span data-id="element-1557">
                                            {item.grMatches.reduce((sum, gr) => sum + gr.allocatedQuantity, 0)}{' '}
                                            total units received and verified
                                          </span>
                                        </div>
                                      </div>}

                                  {/* Backward compatibility: single GR */}
                                  {item.grMatch && !item.grMatches && <div className="flex-1 border-l border-blue-200 pl-6" data-id="element-1558">
                                      <div className="flex items-center justify-between mb-3" data-id="element-1559">
                                        <div className="flex items-center gap-2" data-id="element-1560">
                                          <div className="w-1 h-6 bg-green-500 rounded" data-id="element-1561"></div>
                                          <h4 className="text-sm font-semibold text-green-900" data-id="element-1562">
                                            Goods Receipt
                                          </h4>
                                        </div>
                                        {isEditMode && <button onClick={() => handlePOMatchClick(index)} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-green-700 bg-green-100 hover:bg-green-200 rounded transition-colors" data-id="element-1563">
                                            <PencilIcon className="w-3.5 h-3.5" data-id="element-1564" />
                                            Edit GR Match
                                          </button>}
                                      </div>
                                      <div className="grid grid-cols-3 gap-4 text-xs" data-id="element-1565">
                                        <div data-id="element-1566">
                                          <div className="text-green-700 font-medium mb-1" data-id="element-1567">
                                            GR Number
                                          </div>
                                          <code className="text-sm font-mono bg-green-100 text-green-800 px-2 py-1 rounded border border-green-200" data-id="element-1568">
                                            {item.grMatch.grNumber}
                                          </code>
                                        </div>
                                        <div data-id="element-1569">
                                          <div className="text-green-700 font-medium mb-1" data-id="element-1570">
                                            Receipt Date
                                          </div>
                                          <div className="text-gray-900 font-semibold" data-id="element-1571">
                                            {item.grMatch.grDate}
                                          </div>
                                        </div>
                                        <div data-id="element-1572">
                                          <div className="text-green-700 font-medium mb-1" data-id="element-1573">
                                            Quantity Received
                                          </div>
                                          <div className="text-green-700 font-semibold" data-id="element-1574">
                                            {item.grMatch.grQuantity}
                                          </div>
                                        </div>
                                      </div>
                                      <div className="mt-3 flex items-center gap-1 text-xs text-green-700 font-medium" data-id="element-1575">
                                        <PackageIcon className="w-4 h-4" data-id="element-1576" />
                                        <span data-id="element-1577">
                                          Goods physically received and verified
                                        </span>
                                      </div>
                                    </div>}
                                </div>
                              </td>
                            </tr>}

                          {/* Match to PO prompt - only for existing lines */}
                          {!isNewLine && !hasMatching && (matchingType === '2-way' || matchingType === '3-way') && isEditMode && <tr className="bg-amber-50 border-b border-amber-100" data-id="element-1578">
                                {(matchingType === '2-way' || matchingType === '3-way') && <td className="px-4 py-3" data-id="element-1579"></td>}
                                <td colSpan={isEditMode ? 11 : 10} className="px-4 py-3" data-id="element-1580">
                                  <div className="flex items-center justify-between" data-id="element-1581">
                                    <div className="flex items-center gap-2 text-sm text-amber-800" data-id="element-1582">
                                      <AlertCircleIcon className="w-4 h-4" data-id="element-1583" />
                                      <span data-id="element-1584">
                                        This line is not matched to a Purchase
                                        Order
                                      </span>
                                    </div>
                                    <button onClick={() => handlePOMatchClick(index)} className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 hover:underline font-medium transition-colors" data-id="element-1585">
                                      <LinkIcon className="w-4 h-4" data-id="element-1586" />
                                      Match to PO
                                    </button>
                                  </div>
                                </td>
                              </tr>}
                        </Fragment>;
                })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Summary Footer - Only show when expanded */}
            <div className="mt-6 pt-4 border-t border-gray-200" data-id="element-1587">
              <div className="flex items-center justify-between" data-id="element-1588">
                <div className="flex items-center gap-8 text-sm" data-id="element-1589">
                  <div className="flex items-baseline gap-2" data-id="element-1590">
                    <span className="text-gray-600" data-id="element-1591">Lines:</span>
                    <span className="font-semibold text-gray-900 text-base" data-id="element-1592">
                      {displayLineItems.length}
                    </span>
                  </div>

                  <div className="flex items-baseline gap-2" data-id="element-1593">
                    <span className="text-gray-600" data-id="element-1594">Subtotal:</span>
                    <span className="font-semibold text-gray-900" data-id="element-1595">
                      {formatCurrency(document.subtotal, document.currency, true)}
                    </span>
                  </div>

                  <div className="flex items-baseline gap-2" data-id="element-1596">
                    <span className="text-gray-600" data-id="element-1597">Tax:</span>
                    <span className="font-semibold text-gray-900" data-id="element-1598">
                      {formatCurrency(document.taxAmt, document.currency, true)}
                    </span>
                  </div>

                  <div className="flex items-baseline gap-2" data-id="element-1599">
                    <span className="text-gray-600" data-id="element-1600">Total:</span>
                    <span className="font-bold text-lg text-gray-900" data-id="element-1601">
                      {formatCurrency(document.total, document.currency, true)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>}
      </div>

      {/* Modals */}
      {selectedLineItemIndex !== null && <GLCodingModal currentGLCode={selectedLineItemIndex === -1 ? newLineItem?.glCode || '' : document.lineItems[selectedLineItemIndex]?.glCode || ''} lineItemDescription={selectedLineItemIndex === -1 ? newLineItem?.description || '' : document.lineItems[selectedLineItemIndex]?.description || ''} onApply={handleGLCodeApply} onClose={handleGLCodeClose} data-id="element-1602" />}

      {showBulkGLModal && <BulkGLCodingModal lineItemCount={document.lineItems.length} vendor={document.supplier} onApply={handleBulkGLApply} onClose={() => setShowBulkGLModal(false)} data-id="element-1603" />}

      {showPreviousCodingModal && <PreviousCodingModal vendor={document.supplier} lineItemCount={document.lineItems.length} onApply={handlePreviousCodingApply} onClose={() => setShowPreviousCodingModal(false)} data-id="element-1604" />}

      {poMatchingLineIndex !== null && matchingType && matchingType !== 'non-po' && <POMatchingModal vendorName={document.supplier} invoiceTotal={document.total} invoiceCurrency={document.currency} onApply={(poNumber, selectedLines, matchingType) => {
      console.log('PO Matching applied:', {
        poNumber,
        selectedLines,
        matchingType
      });
      // TODO: Apply the selected PO lines to this specific line item
      setPOMatchingLineIndex(null);
    }} onClose={handlePOMatchClose} data-id="element-1605" />}
    </>;
}